/**
 * Cache-status band along the bottom of a media clip.
 *
 * Unlike the buffered indicator this replaces, the state shown here is durable:
 * it reads the clip cache (backed by the Cache API), so a cached clip stays
 * marked across reloads and only changes when something is actually cached or
 * evicted. Nothing here polls, and nothing flickers.
 */
import { useClipCacheStore } from '../../state/clipcache';
import type { AudioClip, VideoClip } from '../../format/types';

export function CacheBar({ clip }: { clip: VideoClip | AudioClip }) {
  const status = useClipCacheStore((s) => s.byClip[clip.id]);
  if (!status || status.kind === 'none') return null;

  if (status.kind === 'cached') {
    return <div className="tl-cache tl-cache-done" title="Cached — scrubs and plays locally" />;
  }
  if (status.kind === 'queued') {
    return <div className="tl-cache tl-cache-queued" title="Queued for caching" />;
  }
  if (status.kind === 'error') {
    return <div className="tl-cache tl-cache-error" title={`Could not cache: ${status.message}`} />;
  }
  return (
    <div className="tl-cache" title={`Caching — ${Math.round(status.progress * 100)}%`}>
      <span className="tl-cache-fill" style={{ width: `${status.progress * 100}%` }} />
    </div>
  );
}
